import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import CreatableSelect from "@/components/CreatableSelect";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Receipt,
    Plus,
    PencilSimple,
    Trash,
    UploadSimple,
    DownloadSimple,
    LinkSimple,
    CheckCircle,
    Circle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const eur = (n) =>
    new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
    }).format(Number(n) || 0);

const CURRENCIES = ["EUR", "USD", "GBP", "MXN"];
const CURRENCY_SYMBOL = { EUR: "€", USD: "$", GBP: "£", MXN: "$" };

// Importe original en su moneda
const money = (n, cur = "EUR") => {
    try {
        return new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: cur,
            minimumFractionDigits: 2,
        }).format(Number(n) || 0);
    } catch {
        return `${(Number(n) || 0).toFixed(2)} ${cur}`;
    }
};

// Importe consolidado en euros (con fallback a importe original si no hay _eur)
const eurOf = (inv, field) => {
    const v = inv[`${field}_eur`];
    return v != null ? v : inv[field];
};

// Color consistente por forma de pago
const PAY_COLORS = [
    "bg-sky-100 text-sky-700 border-sky-200",
    "bg-violet-100 text-violet-700 border-violet-200",
    "bg-emerald-100 text-emerald-700 border-emerald-200",
    "bg-amber-100 text-amber-700 border-amber-200",
    "bg-rose-100 text-rose-700 border-rose-200",
    "bg-teal-100 text-teal-700 border-teal-200",
    "bg-indigo-100 text-indigo-700 border-indigo-200",
    "bg-orange-100 text-orange-700 border-orange-200",
    "bg-cyan-100 text-cyan-700 border-cyan-200",
    "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
];
const payColor = (m) => {
    if (!m) return "bg-slate-50 text-slate-400 border-slate-200";
    const s = String(m).trim().toLowerCase();
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return PAY_COLORS[h % PAY_COLORS.length];
};

const emptyForm = {
    date: "",
    supplier: "",
    invoice_number: "",
    project_code: "",
    grupo: "",
    subgrupo: "",
    base_amount: "",
    total_amount: "",
    currency: "EUR",
    exchange_rate: "",
    payment_method: "",
    due_date: "",
    payment_date: "",
    concept: "",
    comments: "",
};

export default function AdminSupplierInvoices() {
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [projects, setProjects] = useState([]);
    const [busy, setBusy] = useState(true);
    const [year, setYear] = useState("all");
    const [paidFilter, setPaidFilter] = useState("all");
    const [projectFilter, setProjectFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importReport, setImportReport] = useState(null);
    const [fxRates, setFxRates] = useState({ EUR: 1, USD: 0.92, GBP: 1.17, MXN: 0.05 });
    const [options, setOptions] = useState({
        suppliers: [],
        grupos: [],
        subgrupos: [],
        payment_methods: [],
    });
    const fileRef = useRef(null);

    useEffect(() => {
        http.get("/projects").then((r) => setProjects(r.data));
        http.get("/settings").then((r) => {
            if (r.data?.fx_rates) setFxRates(r.data.fx_rates);
        });
        http.get("/supplier-invoices/options").then((r) => setOptions(r.data));
    }, []);

    const loadSummary = useCallback(() => {
        const params = year !== "all" ? { year } : {};
        http.get("/supplier-invoices/summary", { params }).then((r) =>
            setSummary(r.data),
        );
    }, [year]);

    const load = useCallback(async () => {
        setBusy(true);
        const params = {};
        if (year !== "all") params.year = year;
        if (paidFilter !== "all") params.paid = paidFilter === "paid";
        if (projectFilter !== "all") params.project_id = projectFilter;
        if (search.trim()) params.search = search.trim();
        try {
            const r = await http.get("/supplier-invoices", { params });
            setItems(r.data);
        } finally {
            setBusy(false);
        }
    }, [year, paidFilter, projectFilter, search]);

    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const openNew = () => {
        setEditing(null);
        setForm(emptyForm);
        setOpen(true);
    };
    const openEdit = (inv) => {
        setEditing(inv);
        setForm({
            date: inv.date || "",
            supplier: inv.supplier || "",
            invoice_number: inv.invoice_number || "",
            project_code: inv.project_code || "",
            grupo: inv.grupo || "",
            subgrupo: inv.subgrupo || "",
            base_amount: inv.base_amount ?? "",
            total_amount: inv.total_amount ?? "",
            currency: inv.currency || "EUR",
            exchange_rate: inv.exchange_rate != null ? inv.exchange_rate : "",
            payment_method: inv.payment_method || "",
            due_date: inv.due_date || "",
            payment_date: inv.payment_date || "",
            concept: inv.concept || "",
            comments: inv.comments || "",
        });
        setOpen(true);
    };

    const save = async () => {
        if (!form.supplier.trim()) {
            toast.error("El proveedor es obligatorio");
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            base_amount: form.base_amount === "" ? null : Number(form.base_amount),
            total_amount: form.total_amount === "" ? null : Number(form.total_amount),
            exchange_rate:
                form.currency === "EUR" || form.exchange_rate === ""
                    ? null
                    : Number(form.exchange_rate),
            date: form.date || null,
            due_date: form.due_date || null,
            payment_date: form.payment_date || null,
        };
        try {
            if (editing) {
                await http.put(`/supplier-invoices/${editing.invoice_id}`, payload);
                toast.success("Factura actualizada");
            } else {
                await http.post("/supplier-invoices", payload);
                toast.success("Factura registrada");
            }
            setOpen(false);
            load();
            loadSummary();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        try {
            await http.delete(`/supplier-invoices/${deleteTarget.invoice_id}`);
            toast.success("Factura eliminada");
            setDeleteTarget(null);
            load();
            loadSummary();
        } catch {
            toast.error("Error al eliminar");
        }
    };

    const [exporting, setExporting] = useState(false);
    const exportExcel = async () => {
        setExporting(true);
        try {
            const params = {};
            if (year !== "all") params.year = year;
            if (paidFilter !== "all") params.paid = paidFilter === "paid";
            if (projectFilter !== "all") params.project_id = projectFilter;
            if (search.trim()) params.search = search.trim();
            const res = await http.get("/supplier-invoices/export.xlsx", {
                params,
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `facturas_soportadas_${year !== "all" ? year : "todas"}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Excel exportado");
        } catch {
            toast.error("No se pudo exportar el Excel");
        } finally {
            setExporting(false);
        }
    };

    const togglePaid = async (inv) => {
        // Optimista
        setItems((prev) =>
            prev.map((x) =>
                x.invoice_id === inv.invoice_id
                    ? { ...x, paid: !x.paid }
                    : x,
            ),
        );
        try {
            await http.patch(`/supplier-invoices/${inv.invoice_id}/toggle-paid`);
            loadSummary();
        } catch {
            toast.error("No se pudo cambiar el estado");
            load();
        }
    };

    const onImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        setImportReport(null);
        const fd = new FormData();
        fd.append("file", file);        try {
            const r = await http.post("/supplier-invoices/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setImportReport(r.data);
            toast.success(
                `Importadas ${r.data.parsed} facturas (${r.data.linked_to_projects} asociadas a proyectos)`,
            );
            load();
            loadSummary();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error en la importación");
        } finally {
            setImporting(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const sortedProjects = useMemo(
        () =>
            [...projects].sort((a, b) =>
                (a.project_code || "").localeCompare(b.project_code || ""),
            ),
        [projects],
    );
    const projectOptions = useMemo(() => {
        const opts = sortedProjects
            .filter((p) => p.project_code)
            .map((p) => ({
                value: p.project_code,
                label: p.name || p.project_code,
                hint: p.project_code,
            }));
        // Mantener un código ya asignado aunque no exista como proyecto
        if (form.project_code && !opts.some((o) => o.value === form.project_code)) {
            opts.unshift({
                value: form.project_code,
                label: form.project_code,
                hint: "(sin proyecto)",
            });
        }
        return opts;
    }, [sortedProjects, form.project_code]);
    const paymentOptions = useMemo(() => {
        const defaults = [
            "Domiciliado",
            "Transferencia",
            "Tarjeta",
            "Metálico",
            "Cheque",
        ];
        return Array.from(
            new Set([...defaults, ...(options.payment_methods || [])]),
        );
    }, [options.payment_methods]);
    const years = summary?.years || [];
    const t = summary?.totals || {};

    const accessors = useMemo(
        () => ({
            date: (i) => i.date || "",
            supplier: (i) => (i.supplier || "").toLowerCase(),
            invoice_number: (i) => (i.invoice_number || "").toLowerCase(),
            project: (i) => i.project_code || "",
            grupo: (i) => `${i.grupo || ""}${i.subgrupo || ""}`,
            base: (i) => {
                const v = eurOf(i, "base_amount");
                return v == null ? null : Number(v);
            },
            total: (i) => {
                const v = eurOf(i, "total_amount");
                return v == null ? null : Number(v);
            },
            paid: (i) => (i.paid ? 1 : 0),
        }),
        [],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        items,
        "admin-supplier-invoices",
        accessors,
        { initialKey: "date", initialDir: "desc" },
    );

    return (
        <div data-testid="page-admin-supplier-invoices">
            <PageHeader
                eyebrow="Administración"
                title="Facturas soportadas"
                description="Registro de facturas recibidas de proveedores. Las que tienen código de proyecto se asocian automáticamente."
                actions={
                    <div className="flex items-center gap-2">
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".xlsx"
                            className="hidden"
                            onChange={onImportFile}
                            data-testid="supplier-import-input"
                        />
                        <Button
                            variant="outline"
                            onClick={() => fileRef.current?.click()}
                            disabled={importing}
                            className="rounded-sm border-slate-300"
                            data-testid="supplier-import-btn"
                        >
                            <UploadSimple size={15} className="mr-1.5" />
                            {importing ? "Importando…" : "Importar Excel"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={exportExcel}
                            disabled={exporting}
                            className="rounded-sm border-slate-300"
                            data-testid="supplier-export-btn"
                        >
                            <DownloadSimple size={15} className="mr-1.5" />
                            {exporting ? "Exportando…" : "Exportar Excel"}
                        </Button>
                        <Button
                            onClick={openNew}
                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="supplier-new-btn"
                        >
                            <Plus size={15} className="mr-1.5" /> Nueva factura
                        </Button>
                    </div>
                }
            />
            <div className="p-6 lg:p-10 space-y-6">
                {importReport && (
                    <div className="border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                        Importación completada: <b>{importReport.parsed}</b> facturas,{" "}
                        <b>{importReport.linked_to_projects}</b> asociadas a proyectos.
                        {importReport.unmatched_project_codes?.length > 0 && (
                            <span className="text-emerald-800">
                                {" "}
                                Códigos sin proyecto:{" "}
                                {importReport.unmatched_project_codes.slice(0, 15).join(", ")}
                                {importReport.unmatched_project_codes.length > 15 ? "…" : ""}
                            </span>
                        )}
                    </div>
                )}

                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                    <SummaryCard label="Total facturas" value={t.count ?? 0} mono />
                    <SummaryCard label="Importe total" value={eur(t.total)} mono />
                    <SummaryCard
                        label="Pendientes de pago"
                        value={t.pending ?? 0}
                        sub={eur(t.pending_amount)}
                        mono
                        accent="amber"
                    />
                    <SummaryCard label="Base imponible" value={eur(t.base)} mono />
                </div>

                {/* Filters */}
                <div className="border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3">
                    <div className="min-w-[110px]">
                        <Label className="text-xs">Año</Label>
                        <Select value={String(year)} onValueChange={(v) => setYear(v)}>
                            <SelectTrigger
                                className="mt-1 rounded-sm border-slate-300 h-8"
                                data-testid="supplier-year-filter"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                {years.map((y) => (
                                    <SelectItem key={y} value={String(y)}>
                                        {y}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="min-w-[140px]">
                        <Label className="text-xs">Estado</Label>
                        <Select value={paidFilter} onValueChange={setPaidFilter}>
                            <SelectTrigger
                                className="mt-1 rounded-sm border-slate-300 h-8"
                                data-testid="supplier-paid-filter"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas</SelectItem>
                                <SelectItem value="paid">Pagadas</SelectItem>
                                <SelectItem value="pending">Pendientes</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="min-w-[220px]">
                        <Label className="text-xs">Proyecto</Label>
                        <SearchableSelect
                            value={projectFilter === "all" ? "" : projectFilter}
                            onChange={(v) => setProjectFilter(v || "all")}
                            options={sortedProjects.map((p) => ({
                                value: p.project_id,
                                label: p.name,
                                hint: p.project_code || "",
                            }))}
                            placeholder="Todos"
                            className="mt-1 h-8 !py-1.5"
                            data-testid="supplier-project-filter"
                        />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <Label className="text-xs">Buscar</Label>
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Proveedor, nº factura, concepto…"
                            className="mt-1 rounded-sm border-slate-300 h-8"
                            data-testid="supplier-search"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid="supplier-invoices-table">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("date", "py-3 px-4 text-left")}>
                                    Fecha
                                    <SortIndicator active={sortKey === "date"} dir={sortDir} />
                                </th>
                                <th {...headerProps("supplier", "py-3 px-4 text-left")}>
                                    Proveedor
                                    <SortIndicator
                                        active={sortKey === "supplier"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("invoice_number", "py-3 px-4 text-left")}>
                                    Nº factura
                                    <SortIndicator
                                        active={sortKey === "invoice_number"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("project", "py-3 px-4 text-left")}>
                                    Proyecto
                                    <SortIndicator
                                        active={sortKey === "project"}
                                        dir={sortDir}
                                    />
                                </th>
                                <th {...headerProps("grupo", "py-3 px-4 text-left")}>
                                    Grupo
                                    <SortIndicator active={sortKey === "grupo"} dir={sortDir} />
                                </th>
                                <th className="py-3 px-4 text-left">Forma de pago</th>
                                <th {...headerProps("base", "py-3 px-4 text-right")}>
                                    Base
                                    <SortIndicator active={sortKey === "base"} dir={sortDir} />
                                </th>
                                <th {...headerProps("total", "py-3 px-4 text-right")}>
                                    Total
                                    <SortIndicator active={sortKey === "total"} dir={sortDir} />
                                </th>
                                <th {...headerProps("paid", "py-3 px-4 text-center")}>
                                    Estado
                                    <SortIndicator active={sortKey === "paid"} dir={sortDir} />
                                </th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {busy && (
                                <tr>
                                    <td colSpan={10} className="text-center py-12 text-slate-500">
                                        Cargando…
                                    </td>
                                </tr>
                            )}
                            {!busy && items.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="text-center py-12 text-slate-500">
                                        <Receipt
                                            size={28}
                                            className="mx-auto text-slate-300 mb-3"
                                        />
                                        No hay facturas con estos filtros.
                                    </td>
                                </tr>
                            )}
                            {!busy &&
                                sorted.map((inv) => (
                                    <tr
                                        key={inv.invoice_id}
                                        className="border-b border-slate-100 hover:bg-slate-50"
                                        data-testid={`supplier-row-${inv.invoice_id}`}
                                    >
                                        <td className="py-2.5 px-4 font-mono text-slate-700 whitespace-nowrap">
                                            {inv.date || "—"}
                                        </td>
                                        <td className="py-2.5 px-4 text-slate-800 font-medium max-w-xs truncate">
                                            {inv.supplier}
                                        </td>
                                        <td className="py-2.5 px-4 font-mono text-xs text-slate-600">
                                            {inv.invoice_number || "—"}
                                        </td>
                                        <td className="py-2.5 px-4 text-xs">
                                            {inv.project_id ? (
                                                <span className="inline-flex items-center gap-1 text-[#002BAF]">
                                                    <LinkSimple size={12} />
                                                    <span
                                                        className="truncate max-w-[140px]"
                                                        title={inv.project_name}
                                                    >
                                                        {inv.project_code}
                                                    </span>
                                                </span>
                                            ) : inv.project_code ? (
                                                <span
                                                    className="text-amber-600"
                                                    title="Código sin proyecto asociado"
                                                >
                                                    {inv.project_code}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-4 text-xs text-slate-600 font-mono">
                                            {inv.grupo
                                                ? `${inv.grupo}${
                                                      inv.subgrupo ? " / " + inv.subgrupo : ""
                                                  }`
                                                : "—"}
                                        </td>
                                        <td className="py-2.5 px-4 text-xs">
                                            {inv.payment_method ? (
                                                <span
                                                    className={`inline-block px-2 py-0.5 rounded-sm border text-[11px] font-medium whitespace-nowrap ${payColor(
                                                        inv.payment_method,
                                                    )}`}
                                                    data-testid={`supplier-pay-${inv.invoice_id}`}
                                                >
                                                    {inv.payment_method}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-4 text-right font-mono tabular-nums text-slate-600">
                                            {eurOf(inv, "base_amount") != null
                                                ? eur(eurOf(inv, "base_amount"))
                                                : "—"}
                                        </td>
                                        <td className="py-2.5 px-4 text-right font-mono tabular-nums text-slate-900 font-semibold">
                                            {eurOf(inv, "total_amount") != null ? (
                                                <span className="inline-flex items-center gap-1.5 justify-end">
                                                    {inv.currency && inv.currency !== "EUR" && (
                                                        <span
                                                            className="text-[9px] font-semibold text-slate-400 bg-slate-100 px-1 py-0.5 rounded"
                                                            title={`Importe original: ${money(
                                                                inv.total_amount,
                                                                inv.currency,
                                                            )}`}
                                                            data-testid={`supplier-cur-${inv.invoice_id}`}
                                                        >
                                                            {inv.currency}
                                                        </span>
                                                    )}
                                                    {eur(eurOf(inv, "total_amount"))}
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="py-2.5 px-4 text-center">
                                            <button
                                                type="button"
                                                onClick={() => togglePaid(inv)}
                                                title="Clic para cambiar el estado de pago"
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium border transition-colors cursor-pointer ${
                                                    inv.paid
                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                                        : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                                                }`}
                                                data-testid={`supplier-togglepaid-${inv.invoice_id}`}
                                            >
                                                {inv.paid ? (
                                                    <CheckCircle size={12} weight="fill" />
                                                ) : (
                                                    <Circle size={12} />
                                                )}
                                                {inv.paid ? "Pagada" : "Pendiente"}
                                            </button>
                                        </td>
                                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => openEdit(inv)}
                                                className="text-slate-400 hover:text-[#002BAF] p-1"
                                                data-testid={`supplier-edit-${inv.invoice_id}`}
                                            >
                                                <PencilSimple size={16} />
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(inv)}
                                                className="text-slate-400 hover:text-rose-600 p-1"
                                                data-testid={`supplier-delete-${inv.invoice_id}`}
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                {!busy && items.length >= 2000 && (
                    <p className="text-xs text-slate-500">
                        Mostrando las primeras 2000 facturas. Usa los filtros para acotar.
                    </p>
                )}
            </div>

            {/* Form sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    className="rounded-none sm:max-w-lg overflow-y-auto"
                    data-testid="supplier-form-sheet"
                >
                    <SheetHeader>
                        <SheetTitle className="font-display">
                            {editing ? "Editar factura" : "Nueva factura soportada"}
                        </SheetTitle>
                        <SheetDescription className="text-xs text-slate-500">
                            El código de proyecto (formato 2026_085) asocia la factura al
                            proyecto automáticamente.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Fecha">
                                <Input
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                                    className="rounded-sm border-slate-300 font-mono"
                                    data-testid="supplier-f-date"
                                />
                            </Field>
                            <Field label="Nº factura">
                                <Input
                                    value={form.invoice_number}
                                    onChange={(e) =>
                                        setForm({ ...form, invoice_number: e.target.value })
                                    }
                                    className="rounded-sm border-slate-300 font-mono"
                                    data-testid="supplier-f-number"
                                />
                            </Field>
                        </div>
                        <Field label="Proveedor *">
                            <CreatableSelect
                                value={form.supplier}
                                onChange={(v) => setForm({ ...form, supplier: v })}
                                options={options.suppliers}
                                placeholder="Seleccionar o crear proveedor…"
                                allowClear={false}
                                data-testid="supplier-f-supplier"
                            />
                        </Field>
                        <Field label="Proyecto">
                            <SearchableSelect
                                value={form.project_code}
                                onChange={(v) => setForm({ ...form, project_code: v })}
                                options={projectOptions}
                                placeholder="Buscar por código o nombre…"
                                data-testid="supplier-f-project"
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Grupo">
                                <CreatableSelect
                                    value={form.grupo}
                                    onChange={(v) => setForm({ ...form, grupo: v })}
                                    options={options.grupos}
                                    placeholder="Seleccionar o crear…"
                                    data-testid="supplier-f-grupo"
                                />
                            </Field>
                            <Field label="Subgrupo">
                                <CreatableSelect
                                    value={form.subgrupo}
                                    onChange={(v) => setForm({ ...form, subgrupo: v })}
                                    options={options.subgrupos}
                                    placeholder="Seleccionar o crear…"
                                    data-testid="supplier-f-subgrupo"
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Moneda">
                                <Select
                                    value={form.currency}
                                    onValueChange={(v) =>
                                        setForm((f) => ({
                                            ...f,
                                            currency: v,
                                            exchange_rate:
                                                v === "EUR"
                                                    ? ""
                                                    : f.exchange_rate === ""
                                                      ? String(fxRates[v] ?? "")
                                                      : f.exchange_rate,
                                        }))
                                    }
                                >
                                    <SelectTrigger
                                        className="rounded-sm border-slate-300 font-mono"
                                        data-testid="supplier-f-currency"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CURRENCIES.map((c) => (
                                            <SelectItem key={c} value={c}>
                                                {c} ({CURRENCY_SYMBOL[c]})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            {form.currency !== "EUR" && (
                                <Field label={`Tipo de cambio (1 ${form.currency} = € )`}>
                                    <Input
                                        type="number"
                                        step="0.0001"
                                        value={form.exchange_rate}
                                        onChange={(e) =>
                                            setForm({ ...form, exchange_rate: e.target.value })
                                        }
                                        placeholder={String(fxRates[form.currency] ?? "")}
                                        className="rounded-sm border-slate-300 font-mono"
                                        data-testid="supplier-f-rate"
                                    />
                                </Field>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field
                                label={`Base imponible (${CURRENCY_SYMBOL[form.currency] || "€"})`}
                            >
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.base_amount}
                                    onChange={(e) =>
                                        setForm({ ...form, base_amount: e.target.value })
                                    }
                                    className="rounded-sm border-slate-300 font-mono"
                                    data-testid="supplier-f-base"
                                />
                            </Field>
                            <Field
                                label={`Total factura (${CURRENCY_SYMBOL[form.currency] || "€"})`}
                            >
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.total_amount}
                                    onChange={(e) =>
                                        setForm({ ...form, total_amount: e.target.value })
                                    }
                                    className="rounded-sm border-slate-300 font-mono"
                                    data-testid="supplier-f-total"
                                />
                            </Field>
                        </div>
                        {form.currency !== "EUR" && (
                            <div
                                className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between"
                                data-testid="supplier-f-eur-preview"
                            >
                                <span>Equivalente consolidado en euros</span>
                                <span className="font-mono font-semibold text-slate-900">
                                    {form.total_amount === ""
                                        ? "—"
                                        : eur(
                                              Number(form.total_amount) *
                                                  (Number(form.exchange_rate) ||
                                                      fxRates[form.currency] ||
                                                      1),
                                          )}
                                </span>
                            </div>
                        )}
                        <Field label="Forma de pago">
                            <CreatableSelect
                                value={form.payment_method}
                                onChange={(v) => setForm({ ...form, payment_method: v })}
                                options={paymentOptions}
                                placeholder="Domiciliado, Transferencia, Tarjeta…"
                                data-testid="supplier-f-payment"
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Vencimiento">
                                <Input
                                    type="date"
                                    value={form.due_date}
                                    onChange={(e) =>
                                        setForm({ ...form, due_date: e.target.value })
                                    }
                                    className="rounded-sm border-slate-300 font-mono"
                                    data-testid="supplier-f-due"
                                />
                            </Field>
                            <Field label="Fecha de pago">
                                <Input
                                    type="date"
                                    value={form.payment_date}
                                    onChange={(e) =>
                                        setForm({ ...form, payment_date: e.target.value })
                                    }
                                    className="rounded-sm border-slate-300 font-mono"
                                    data-testid="supplier-f-paydate"
                                />
                            </Field>
                        </div>
                        <Field label="Concepto">
                            <Input
                                value={form.concept}
                                onChange={(e) =>
                                    setForm({ ...form, concept: e.target.value })
                                }
                                className="rounded-sm border-slate-300"
                                data-testid="supplier-f-concept"
                            />
                        </Field>
                        <Field label="Comentarios">
                            <Input
                                value={form.comments}
                                onChange={(e) =>
                                    setForm({ ...form, comments: e.target.value })
                                }
                                className="rounded-sm border-slate-300"
                                data-testid="supplier-f-comments"
                            />
                        </Field>
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="supplier-f-save"
                        >
                            {saving ? "Guardando…" : editing ? "Guardar cambios" : "Registrar"}
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(v) => !v && setDeleteTarget(null)}
            >
                <AlertDialogContent className="rounded-sm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar factura?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Se eliminará la factura de {deleteTarget?.supplier}
                            {deleteTarget?.invoice_number
                                ? ` (${deleteTarget.invoice_number})`
                                : ""}
                            . Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-sm bg-rose-600 hover:bg-rose-700"
                            onClick={(e) => {
                                e.preventDefault();
                                doDelete();
                            }}
                            data-testid="supplier-confirm-delete"
                        >
                            Eliminar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SummaryCard({ label, value, sub, mono, accent }) {
    return (
        <div className="bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {label}
            </div>
            <div
                className={`mt-2 text-2xl font-bold ${mono ? "font-mono" : ""} ${
                    accent === "amber" ? "text-amber-600" : "text-slate-950"
                }`}
            >
                {value}
            </div>
            {sub && <div className="text-xs text-slate-500 mt-0.5 font-mono">{sub}</div>}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
